import { create } from 'zustand'

// ─── State shape ──────────────────────────────────────────────────────────────

export type BookingMode = 'single' | 'trial' | 'package'

interface BookingState {
  // Data
  instructorId: string | null
  selectedDate: Date | null
  selectedTime: string | null
  durationMinutes: number
  pickupAddress: string
  pickupLat: number | null
  pickupLng: number | null
  notes: string
  bookingMode: BookingMode
  /** Pacote do aluno (student_packages.id) usado quando bookingMode = 'package'. */
  studentPackageId: string | null
  giftCardCode: string | null
  giftCardId: string | null
  giftCardAmount: number | null

  // Actions
  setInstructor: (id: string) => void
  setDate: (date: Date) => void
  setTime: (time: string) => void
  setDuration: (minutes: number) => void
  setPickup: (address: string, lat?: number, lng?: number) => void
  setNotes: (notes: string) => void
  setBookingMode: (mode: BookingMode) => void
  setStudentPackageId: (id: string | null) => void
  setGiftCard: (code: string | null, id: string | null, amount: number | null) => void
  reset: () => void
}

// ─── Default / initial state ──────────────────────────────────────────────────

const initialState = {
  instructorId: null,
  selectedDate: null,
  selectedTime: null,
  durationMinutes: 60,
  pickupAddress: '',
  pickupLat: null,
  pickupLng: null,
  notes: '',
  bookingMode: 'single' as BookingMode,
  studentPackageId: null as string | null,
  giftCardCode: null,
  giftCardId: null,
  giftCardAmount: null,
}

// ─── Store ────────────────────────────────────────────────────────────────────

const useBookingStore = create<BookingState>()((set) => ({
  // ── Initial state ──────────────────────────────────────────────────────────
  ...initialState,

  // ── Setters ────────────────────────────────────────────────────────────────

  setInstructor: (id) => set({ instructorId: id }),

  setDate: (date) => set({ selectedDate: date }),

  setTime: (time) => set({ selectedTime: time }),

  setDuration: (minutes) => set({ durationMinutes: minutes }),

  setPickup: (address, lat, lng) =>
    set({
      pickupAddress: address,
      pickupLat: lat ?? null,
      pickupLng: lng ?? null,
    }),

  setNotes: (notes) => set({ notes }),

  setBookingMode: (mode) => set({ bookingMode: mode }),

  setStudentPackageId: (id) => set({ studentPackageId: id }),

  setGiftCard: (code, id, amount) => set({ giftCardCode: code, giftCardId: id, giftCardAmount: amount }),

  // ── Reset ──────────────────────────────────────────────────────────────────

  reset: () => set({ ...initialState }),
}))

export { useBookingStore }
export type { BookingState }
